import React from 'react';
import './VoiceOrb.css';

/**
 * VoiceOrb — JARVIS arc reactor / HUD style visual indicator.
 *
 * Props:
 *   state — 'idle' | 'ready' | 'listening' | 'processing' | 'speaking'
 */

export default function VoiceOrb({ state = 'idle', wordPulse = false, isDragging = false }) {
  return (
    <div className={`voice-orb-wrapper ${wordPulse ? 'word-pulse' : ''} ${isDragging ? 'dragging' : ''}`}>
      <svg viewBox="0 0 300 300" width="280" height="280" className={`jarvis-reactor ${state}`}>
        <defs>
          <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="30%" stopColor="#00d4ff" stopOpacity="0.7" />
            <stop offset="70%" stopColor="#0099cc" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0099cc" stopOpacity="0" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="glowStrong">
            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer ring — 4 arc segments with gaps */}
        <g className="outer-ring" filter="url(#glow)">
          {/* Arc 1: 0-60 deg */}
          <path
            d="M 150 30 A 120 120 0 0 1 253.92 90"
            fill="none"
            stroke="#00d4ff"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          {/* Arc 2: 90-150 deg */}
          <path
            d="M 270 150 A 120 120 0 0 1 210 253.92"
            fill="none"
            stroke="#00d4ff"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          {/* Arc 3: 180-240 deg */}
          <path
            d="M 150 270 A 120 120 0 0 1 46.08 210"
            fill="none"
            stroke="#00d4ff"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          {/* Arc 4: 270-330 deg */}
          <path
            d="M 30 150 A 120 120 0 0 1 90 46.08"
            fill="none"
            stroke="#00d4ff"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>

        {/* Middle ring — dashed with cardinal dots */}
        <g className="middle-ring" filter="url(#glow)">
          <circle
            cx="150"
            cy="150"
            r="90"
            fill="none"
            stroke="#0099cc"
            strokeWidth="0.5"
            strokeDasharray="8 4"
            opacity="0.6"
          />
          {/* Cardinal point dots */}
          <circle cx="150" cy="60" r="2.5" fill="#00d4ff" opacity="0.8" />
          <circle cx="240" cy="150" r="2.5" fill="#00d4ff" opacity="0.8" />
          <circle cx="150" cy="240" r="2.5" fill="#00d4ff" opacity="0.8" />
          <circle cx="60" cy="150" r="2.5" fill="#00d4ff" opacity="0.8" />
        </g>

        {/* Inner ring — tick marks like a compass */}
        <g className="inner-ring" filter="url(#glow)">
          <circle
            cx="150"
            cy="150"
            r="60"
            fill="none"
            stroke="#00d4ff"
            strokeWidth="1"
          />
          {/* Tick marks every 30 degrees */}
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i * 30 * Math.PI) / 180;
            const x1 = 150 + 55 * Math.cos(angle);
            const y1 = 150 + 55 * Math.sin(angle);
            const x2 = 150 + 60 * Math.cos(angle);
            const y2 = 150 + 60 * Math.sin(angle);
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#00d4ff"
                strokeWidth={i % 3 === 0 ? '2' : '0.8'}
                opacity="0.7"
              />
            );
          })}
        </g>

        {/* Center core glow */}
        <circle
          className="core-glow"
          cx="150"
          cy="150"
          r="30"
          fill="url(#coreGlow)"
          filter="url(#glowStrong)"
        />

        {/* Core bright center */}
        <circle
          className="core-center"
          cx="150"
          cy="150"
          r="12"
          fill="#00d4ff"
          opacity="0.9"
          filter="url(#glowStrong)"
        />
      </svg>
    </div>
  );
}
